import { Controller } from "@hotwired/stimulus";

// Mobile bottom sheet controller for search page
// Manages margin offset states: default, expanded (list focus), collapsed (map focus)
// Uses native page scroll - no separate scroll context
export default class extends Controller {
  static values = {
    state: { type: String, default: "default" },
  };
  static targets = ["handle", "sheet"];

  connect() {
    this.isMobileActive = false;
    this.mapElement = document.getElementById("search-map");

    // Check initial state and listen for resize
    this.checkBreakpoint();
    this.boundOnResize = this.checkBreakpoint.bind(this);
    window.addEventListener("resize", this.boundOnResize);
  }

  disconnect() {
    window.removeEventListener("resize", this.boundOnResize);
    this.deactivateMobile();
  }

  checkBreakpoint() {
    const isMobile = window.innerWidth < 768;

    if (isMobile && !this.isMobileActive) {
      this.activateMobile();
    } else if (!isMobile && this.isMobileActive) {
      this.deactivateMobile();
    }
  }

  activateMobile() {
    this.isMobileActive = true;
    this.bindHandleEvents();
    this.bindMapTapEvents();
    this.bindSheetTapEvents();

    // Set initial interaction state once map is ready
    if (this.mapElement?.dataset.mapReady === "true") {
      requestAnimationFrame(() => this.updateMapInteraction(this.stateValue));
    } else {
      this.boundOnMapReady = () => this.updateMapInteraction(this.stateValue);
      this.mapElement?.addEventListener("map:ready", this.boundOnMapReady);
    }
  }

  deactivateMobile() {
    if (!this.isMobileActive) return;

    this.isMobileActive = false;
    this.unbindHandleEvents();
    this.unbindMapTapEvents();
    this.unbindSheetTapEvents();

    if (this.mapElement && this.boundOnMapReady) {
      this.mapElement.removeEventListener("map:ready", this.boundOnMapReady);
    }

    // Re-enable map interaction and body scroll on desktop
    document.body.style.overflow = "";
    if (this.mapElement?.dataset.mapReady === "true") {
      this.mapElement.dispatchEvent(
        new CustomEvent("map:setInteraction", { detail: { enabled: true } }),
      );
    }
  }

  // Handle tap and swipe gestures
  bindHandleEvents() {
    if (!this.hasHandleTarget) return;

    this.boundOnTouchStart = this.onTouchStart.bind(this);
    this.boundOnTouchEnd = this.onTouchEnd.bind(this);

    this.handleTarget.addEventListener("touchstart", this.boundOnTouchStart, {
      passive: true,
    });
    this.handleTarget.addEventListener("touchend", this.boundOnTouchEnd);
  }

  unbindHandleEvents() {
    if (!this.hasHandleTarget) return;
    this.handleTarget.removeEventListener("touchstart", this.boundOnTouchStart);
    this.handleTarget.removeEventListener("touchend", this.boundOnTouchEnd);
  }

  onTouchStart(e) {
    this.touchStartY = e.touches[0].clientY;
  }

  onTouchEnd(e) {
    const deltaY = e.changedTouches[0].clientY - this.touchStartY;
    const threshold = 30;

    if (deltaY > threshold) {
      // Swipe down - show more map
      this.stateValue =
        this.stateValue === "expanded" ? "default" : "collapsed";
    } else if (deltaY < -threshold) {
      // Swipe up - show more list
      this.stateValue =
        this.stateValue === "collapsed" ? "default" : "expanded";
    }
    // Tap does nothing - use swipe gestures or tap sheet when collapsed
  }

  // Collapse when tapping map to show more of it
  bindMapTapEvents() {
    if (!this.mapElement) return;

    this.boundOnMapTap = this.onMapTap.bind(this);
    this.mapElement.addEventListener("click", this.boundOnMapTap);
  }

  unbindMapTapEvents() {
    if (!this.mapElement || !this.boundOnMapTap) return;
    this.mapElement.removeEventListener("click", this.boundOnMapTap);
  }

  // Tap sheet to exit collapsed state
  bindSheetTapEvents() {
    if (!this.hasSheetTarget) return;

    this.boundOnSheetTap = this.onSheetTap.bind(this);
    this.sheetTarget.addEventListener("click", this.boundOnSheetTap);
  }

  unbindSheetTapEvents() {
    if (!this.hasSheetTarget || !this.boundOnSheetTap) return;
    this.sheetTarget.removeEventListener("click", this.boundOnSheetTap);
  }

  onSheetTap() {
    // Only respond when collapsed - tap to expand
    if (this.stateValue === "collapsed") {
      this.stateValue = "default";
    }
  }

  onMapTap(e) {
    // Don't collapse if user tapped on a marker or callout
    if (e.target.closest(".apple-marker, .apple-callout, .gm-style")) return;

    // Show more map
    if (this.stateValue !== "collapsed") {
      this.stateValue = "collapsed";
    }
  }

  // State change callback
  stateValueChanged(state, previousState) {
    this.updateMapInteraction(state);
    this.updateBodyScroll(state);
  }

  // Disable page scroll when collapsed (requires tap to exit)
  updateBodyScroll(state) {
    if (!this.isMobileActive) return;

    if (state === "collapsed") {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
  }

  // Enable map scroll/zoom only when map is focused (collapsed state)
  updateMapInteraction(state) {
    if (!this.mapElement || !this.isMobileActive) return;

    const enabled = state === "collapsed";
    this.mapElement.dispatchEvent(
      new CustomEvent("map:setInteraction", { detail: { enabled } }),
    );
  }

  // Action methods
  expand() {
    this.stateValue = "expanded";
  }

  collapse() {
    this.stateValue = "collapsed";
  }

  reset() {
    this.stateValue = "default";
  }
}
